import { WarehouseStockDetail } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { LIFECYCLE_W1 } from 'domains/catalog/Catalog.types';
import { WAREHOUSE_STOCKS_ORDER } from 'domains/references/References.types';
import { TireSortReference, TireSortReferencePriceType } from 'domains/tires/Tire.types';
import { GarageView, SparePartsViewType } from 'domains/user';
import { getData, hasPrice, NO_DATA } from 'utils';

const hasValidStocks = (ref: TireSortReference): boolean => !!ref.stocks && ref.stocks?.warehouses.length > 0;

const compareWarehouses = (a: WarehouseStockDetail, b: WarehouseStockDetail): number => {
  const warehouseTypeA = WAREHOUSE_STOCKS_ORDER.indexOf(a.type);
  const warehouseTypeB = WAREHOUSE_STOCKS_ORDER.indexOf(b.type);

  // sort by warehouse type, continue if both warehouses are unknown
  if (!(warehouseTypeA === -1 && warehouseTypeB === -1)) {
    if (warehouseTypeB === -1 || warehouseTypeA > warehouseTypeB) {
      return 1;
    } else if (warehouseTypeA === -1 || warehouseTypeA < warehouseTypeB) {
      return -1;
    }
  }

  // sort by delivery date
  const warehouseDeliveryA = a?.expectedDeliveryDate ?? '';
  const warehouseDeliveryB = b?.expectedDeliveryDate ?? '';

  if (warehouseDeliveryA > warehouseDeliveryB) {
    return -1;
  } else if (warehouseDeliveryA < warehouseDeliveryB) {
    return 1;
  }
  return 0;
};

export const sortReferencesByStocks = (a: TireSortReference, b: TireSortReference): number => {
  const aHasStocks = hasValidStocks(a);
  const bHasStocks = hasValidStocks(b);

  // handle cases where one or both don't have stocks
  if (!aHasStocks && !bHasStocks) {
    return 0;
  }
  if (!aHasStocks) {
    return -1;
  }
  if (!bHasStocks) {
    return 1;
  }

  const warehouseA = a.stocks?.warehouses[0];
  const warehouseB = b.stocks?.warehouses[0];

  //condition shouldn't be needed, but TypeScript doesn't know it was checked in the "hasValidStocks"
  if (warehouseA && warehouseB) {
    return compareWarehouses(warehouseA, warehouseB);
  }
  return 0;
};

export const refSortPrice = (
  sparePartsView: SparePartsViewType,
  item: { reference?: string; prices?: TireSortReferencePriceType | NO_DATA },
): number => {
  return Number(
    sparePartsView === GarageView
      ? getData(item.prices)?.garageView?.vatExcludedPrice
      : getData(item.prices)?.clientView?.discountedRecommendedPriceVatExcluded ??
          getData(item.prices)?.clientView?.recommendedPriceVatExcluded,
  );
};

export const sortReferencesByPrice = (a: TireSortReference, b: TireSortReference): number => {
  const aData = refSortPrice(a.sparePartsView, { prices: a.price });
  const bData = refSortPrice(b.sparePartsView, { prices: b.price });

  if ((!aData && !bData) || aData === bData) {
    return 0;
  } else if (!aData) {
    return -1;
  } else if (!bData) {
    return 1;
  }

  return Number(aData) - Number(bData);
};

export const sortTireReferencesByLifecycle = (a: TireSortReference, b: TireSortReference): number => {
  if (a.lifeCycle === LIFECYCLE_W1 || b.lifeCycle === LIFECYCLE_W1) {
    if (a.lifeCycle === LIFECYCLE_W1 && b.lifeCycle === LIFECYCLE_W1) {
      return 0;
    } else if (a.lifeCycle === LIFECYCLE_W1) {
      return 1;
    } else {
      return -1;
    }
  } else {
    return 0;
  }
};

export const sortTireWithoutPriceLast = (a: TireSortReference, b: TireSortReference): number => {
  const aHasPrice = !!hasPrice(a.sparePartsView, a.price);
  const bHasPrice = !!hasPrice(b.sparePartsView, b.price);

  if (aHasPrice && !bHasPrice) return -1;
  if (!aHasPrice && bHasPrice) return 1;
  return 0;
};
